import { useCallback, useEffect, useState } from "react";
import {
  Box,
  Card,
  Checkbox,
  TextField,
  Typography,
  Button
} from "@mui/material";
import ShowComponents from "../Common/ShowComponent";
import RestAPIService from "../../../../Utility/Services/RestAPIService";
import { Colors } from "../../../../Styles/Colors";
import { renderTitle } from "../Common/DocCommonComponent";
import { Serviceurls } from "../../../../Utility/API/Serviceurls";

const VITAL_ELEMENTS = [
  "Height", "Weight in Kgs", "BP Systolic", "BP Diastolic", "Temperature",
  "SpO2", "Blood Glucose", "Pulse", "Appetite",
  "Bowels", "Sleep", "Urination", "Pallor", "Clubbing",
  "Icterus", "Cyanosis", "Oedema", "Lymph nodes"
];

const DocVitals = ({
  Title,
  appointmentId,
  ipAdmissionId,
  clinicId,
  doctorId,
  isFromIp,
  patientId,
  isSelected,
  successMessage,
  errorMessage
}) => {

  const [state, setState] = useState({
    vitals: [],
    isLoading: false
  });

  const updateState = (obj) =>
    setState((prev) => ({ ...prev, ...obj }));

  /* ================= INITIALIZE ================= */
  const initVitals = () =>
    VITAL_ELEMENTS.map((el) => ({
      element: el.toLowerCase().replace(/\s/g, "_"),
      label: el,
      is_checked: true,
      value: "",
      remarks: ""
    }));

  /* ================= GET ================= */
  const getVitals = useCallback(() => {
    const params = isFromIp ? `?ip_admission_id=${ipAdmissionId}&clinic_id=${clinicId}&doctor_id=${doctorId}` : `?appointment_id=${appointmentId}`;
    RestAPIService.getAll(Serviceurls.DOC_VITALS_GET + params)
      .then((res) => {
        if (res?.data?.status === "success" && res.data.data.vitals?.length) {
          const apiVitals = res.data.data.vitals;

          const merged = initVitals().map((v) => {
            const match = apiVitals.find(
              (a) => a.element === v.element
            );
            return match
              ? { ...v, ...match }
              : v;
          });

          updateState({ vitals: merged });
        } else {
          updateState({ vitals: initVitals() });
        }
      })
      .catch((err) => errorMessage(err?.message));
  }, [appointmentId, isFromIp, ipAdmissionId]);

  /* ================= UPDATE ================= */
  const updateVital = (index, key, value) => {
    const updated = [...state.vitals];
    updated[index][key] = value;
    updateState({ vitals: updated });
  };

  /* ================= SAVE ================= */
  const saveVitals = () => {
    const payload = {
      patient_id: patientId,
      vitals: state?.vitals.map((v) => ({
        element: v.element,
        is_checked: v.is_checked,
        value: v.value,
        remarks: v.remarks
      }))
    };
    if (isFromIp) {
      payload.ip_admission_id = ipAdmissionId;
      payload.clinic_id = clinicId;
      payload.doctor_id = doctorId;
    } else {
      payload.appointment_id = appointmentId;
    }

    updateState({ isLoading: true });

    RestAPIService.create(payload, Serviceurls.DOC_VITALS_GET)
      .then((res) => {
        if (res.data.status === "success") {
          successMessage(res.data.message || "Vitals saved");
          getVitals();
        } else {
          errorMessage(res.data.message);
        }
      })
      .catch((err) =>
        errorMessage(err?.response?.data?.message || err.message)
      )
      .finally(() => updateState({ isLoading: false }));
  };

  useEffect(() => {
    getVitals();
  }, [getVitals]);

  /* ================= UI ================= */
  return (
    <ShowComponents onIntersection={getVitals}>
      <Card
        sx={{
          width: "100%",
          border: `2px solid ${
            isSelected ? Colors.themeDark : Colors.white
          }`,
          padding: "0.5vw"
        }}
      >
        {/* HEADER */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          borderBottom="1px solid #ddd"
          p="0.5vw"
        >
          {renderTitle(Title || "Vitals", isSelected)}

          <Button
            variant="contained"
            onClick={saveVitals}
            disabled={state.isLoading}
          >
            SAVE
          </Button>
        </Box>

        {/* TABLE HEADER */}
        <Box
          display="grid"
          gridTemplateColumns="1fr 0.4fr 0.6fr 1fr 1fr 0.4fr 0.6fr 1fr"
          p="0.4vw"
          fontWeight={600}
          bgcolor="#f5f5f5"
        >
          <Typography>Element</Typography>
          <Typography>Y/N</Typography>
          <Typography>Text Value</Typography>
          <Typography sx={{marginRight: "2dvw"}}>Remarks</Typography>
          <Typography>Element</Typography>
          <Typography>Y/N</Typography>
          <Typography>Text Value</Typography>
          <Typography>Remarks</Typography>
        </Box>

        {/* ROWS */}
        {Array.from({ length: Math.ceil(state.vitals.length / 2) }).map(
          (_, rowIndex) => {
            const left = state.vitals[rowIndex * 2];
            const right = state.vitals[rowIndex * 2 + 1];

            return (
              <Box
                key={rowIndex}
                display="grid"
                gridTemplateColumns="1fr 0.4fr 0.6fr 1fr 1fr 0.4fr 0.6fr 1fr"
                alignItems="center"
                p="0.35vw"
                borderBottom="1px solid #eee"
              >
                {[left, right].map((item, idx) =>
                  item ? (
                    <Box
                      key={idx}
                      display="contents"
                    >
                      <Typography fontSize="0.85vw">
                        {item.label}
                      </Typography>

                      <Checkbox
                        checked={item.is_checked}
                        sx={{alignItems: 'center'}}
                        onChange={(e) =>
                          updateVital(
                            rowIndex * 2 + idx,
                            "is_checked",
                            e.target.checked
                          )
                        }
                      />

                      <TextField
                        size="small"
                        value={item.value}
                        sx={{marginRight: "1dvw"}}
                        disabled={!item.is_checked}
                        inputProps={{maxlength: 10}}
                        onChange={(e) =>
                          updateVital(
                            rowIndex * 2 + idx,
                            "value",
                            e.target.value
                          )
                        }
                      />

                      <TextField
                        size="small"
                        sx={{marginRight: "2dvw"}}
                        value={item.remarks}
                        disabled={!item.is_checked}
                        inputProps={{maxlength: 250}}
                        onChange={(e) =>
                          updateVital(
                            rowIndex * 2 + idx,
                            "remarks",
                            e.target.value
                          )
                        }
                      />
                    </Box>
                  ) : (
                    <Box key={idx} />
                  )
                )}
              </Box>
            );
          }
        )}
      </Card>
    </ShowComponents>
  );
};

export default DocVitals;
